import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar } from '@/components/brand/Brand';
import { Button } from '@/components/ui/Button';
import { CourseTexture } from '@/components/ui/CourseTexture';
import { Dialog } from '@/components/ui/Dialog';
import { Field } from '@/components/ui/Field';
import { Pill } from '@/components/ui/Pill';
import { ChevronLeftIcon, PencilIcon } from '@/components/icons';
import { SectionBar } from '@/components/ui/SectionBar';
import { Sheet, SheetGroup } from '@/components/ui/Sheet';
import { useToast } from '@/components/ui/Toast';
import { usersApi } from '@/lib/api/endpoints';
import { qk, useCourseIndex, useHandicap } from '@/lib/api/queries';
import type { Gender } from '@/lib/api/types';
import { useAuth, useCurrentUser } from '@/lib/auth/AuthContext';
import { cx } from '@/lib/cx';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { fmtDateTime } from '@/lib/format/date';
import { fmtDecimal } from '@/lib/format/number';
import { loadUnit, saveUnit, type DistanceUnit } from '@/lib/geo/units';
import { countingScoreCount, handicapAdjustment } from '@/lib/stats/aggregate';
import { locale, saveLocale, t, tx, type Locale, type PlainMessageKey } from '@/lib/i18n';
import { routes } from '@/routes/paths';
import styles from './Profile.module.css';
import { ChevronDownIcon, ChevronRightIcon } from '@/components/icons';

const GENDER_LABEL: Record<Gender, PlainMessageKey> = {
  M: 'profile.gender.M',
  F: 'profile.gender.F',
  X: 'profile.gender.X',
};

const PROVIDER_LABEL: Record<string, PlainMessageKey> = {
  local: 'profile.provider.local',
  google: 'profile.provider.google',
  apple: 'profile.provider.apple',
};

/** Os idiomas oferecidos, nomeados cada um no próprio idioma. */
const LANGUAGES: Array<{ id: Locale; label: PlainMessageKey }> = [
  { id: 'pt', label: 'profile.language.pt' },
  { id: 'en', label: 'profile.language.en' },
];

export function Profile() {
  const user = useCurrentUser();
  const { logout, reloadUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const handicap = useHandicap();
  const courseIndex = useCourseIndex();

  const [editing, setEditing] = useState(false);
  /*
   * No desktop a tabela do WHS nasce aberta (canvas 1w): no celular ela é longa demais para
   * caber sem esconder, aqui não é — e uma explicação que o jogador precisa abrir para ler é
   * uma explicação que a maioria não lê.
   */
  const [explainerOpen, setExplainerOpen] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 900px)').matches : false,
  );
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [unit, setUnit] = useState<DistanceUnit>(loadUnit);
  const [saving, setSaving] = useState(false);
  const [sharingBusy, setSharingBusy] = useState(false);
  const isDesktop = useIsDesktop();

  const [draftName, setDraftName] = useState(user.displayName);
  const [draftGender, setDraftGender] = useState<Gender>(user.gender);
  const [draftHome, setDraftHome] = useState(user.homeCourseId ?? '');

  const homeCourse = user.homeCourseId ? courseIndex.data?.get(user.homeCourseId) : undefined;
  const basedOn = handicap.data?.basedOnRounds ?? 0;

  const openEditor = () => {
    setDraftName(user.displayName);
    setDraftGender(user.gender);
    setDraftHome(user.homeCourseId ?? '');
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // ValidationPipe runs with forbidNonWhitelisted, so send only what changed and only
      // fields the DTO declares — echoing the whole user object back would be a hard 400.
      await usersApi.updateMe({
        displayName: draftName.trim(),
        gender: draftGender,
        homeCourseId: draftHome === '' ? null : draftHome,
      });
      await reloadUser();
      await queryClient.invalidateQueries({ queryKey: qk.me });
      setEditing(false);
      toast.success(t('profile.updated'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('course.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const setSharing = async (next: boolean) => {
    // A cápsula não tem estado desabilitado, então a reentrância se barra aqui.
    if (sharingBusy || next === user.shareRoundsWithFriends) return;
    setSharingBusy(true);
    try {
      await usersApi.updateMe({ shareRoundsWithFriends: next });
      await reloadUser();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.me }),
        // A timeline muda no mesmo instante, nos dois sentidos.
        queryClient.invalidateQueries({ queryKey: ['feed'] }),
      ]);
      toast.success(next ? t('profile.sharingOn') : t('profile.sharingOff'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('course.saveFailed'));
    } finally {
      setSharingBusy(false);
    }
  };

  const changeUnit = (next: DistanceUnit) => {
    setUnit(next);
    saveUnit(next);
  };

  return (
    <div className={styles.page}>
      <CourseTexture location={homeCourse?.location} veil="header" className={styles.hero}>
        <div className={styles.heroNav}>
          <Link to={routes.home} className={styles.back} aria-label={t('rounds.back')}>
            <ChevronLeftIcon size={22} />
          </Link>
          <button
            type="button"
            className={styles.back}
            onClick={openEditor}
            aria-label={t('profile.edit')}
          >
            <PencilIcon size={18} />
          </button>
        </div>

        <div className={styles.identity}>
          <Avatar name={user.displayName} size={62} variant="gold" />
          <div>
            <h1 className={styles.name}>{user.displayName}</h1>
            <p className={styles.email}>
              {t('profile.memberSince', {
                year: new Date(user.createdAt).getFullYear(),
                email: user.email,
              })}
            </p>
          </div>
        </div>

        <div className={styles.providers}>
          {user.authProviders.map((p) => (
            <span key={p} className={styles.provider}>
              {PROVIDER_LABEL[p] ? t(PROVIDER_LABEL[p]) : p}
            </span>
          ))}
          {/* No desktop o lápis vira palavra: há largura para dizer o que ele faz. */}
          {isDesktop && (
            <button type="button" className={styles.editButton} onClick={openEditor}>
              {t('profile.edit')}
            </button>
          )}
        </div>
      </CourseTexture>

      {/* Duas colunas no desktop; no celular o mesmo markup empilha na ordem de hoje. */}
      <div className={styles.columns}>
        <div className={styles.column}>

      <SectionBar variant="mid">{t('record.handicap')}</SectionBar>
      <section className={styles.handicapCard}>
        <div className={styles.handicapHead}>
          <div>
            <div className={styles.handicapLabel}>{t('home.handicapIndex')}</div>
            <div className={styles.handicapValue}>{fmtDecimal(handicap.data?.handicapIndex)}</div>
            <div className={styles.handicapMeta}>
              {handicapMeta(basedOn, handicap.data?.lastUpdated)}
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            busy={handicap.isFetching}
            onClick={() => void handicap.refetch()}
          >
            {t('profile.recalculate')}
          </Button>
        </div>

        <div className={styles.explainer}>
          <button
            type="button"
            className={styles.explainerToggle}
            onClick={() => setExplainerOpen((v) => !v)}
            aria-expanded={explainerOpen}
          >
            {t('profile.howCalculated')}
            <ChevronDownIcon size={14} className={explainerOpen ? styles.caretUp : undefined} />
          </button>

          {explainerOpen && (
            <>
              <p className={styles.explainerBody}>
                {tx('profile.explainer', {
                  strong: <strong>{t('profile.explainerStrong')}</strong>,
                })}
              </p>
              <WhsTable currentRounds={basedOn} />
              {/* A linha que traduz a tabela para o caso do próprio jogador. */}
              {basedOn >= 3 && (
                <p className={styles.explainerBand}>
                  {t('profile.yourBand', {
                    band: bandLabel(basedOn),
                    base:
                      countingScoreCount(basedOn) === 1
                        ? t('profile.baseLowest')
                        : t('profile.baseAverage', { count: countingScoreCount(basedOn) }),
                  })}
                </p>
              )}
            </>
          )}
        </div>
      </section>
        </div>

        <div className={styles.column}>
      <SectionBar>{t('profile.myData')}</SectionBar>
      <button type="button" className={styles.row} onClick={openEditor}>
        <span className={styles.rowLabel}>{t('profile.displayName')}</span>
        <span className={styles.rowValue}>
                {user.displayName}
                <ChevronRightIcon size={15} />
              </span>
      </button>
      <button type="button" className={styles.row} onClick={openEditor}>
        <span className={styles.rowStacked}>
          <span className={styles.rowLabel}>{t('profile.genderLabel')}</span>
          <span className={styles.rowHint}>{t('profile.genderHint')}</span>
        </span>
        <span className={styles.rowValue}>
                {t(GENDER_LABEL[user.gender])}
                <ChevronRightIcon size={15} />
              </span>
      </button>
      <button type="button" className={styles.row} onClick={openEditor}>
        <span className={styles.rowLabel}>{t('profile.homeCourse')}</span>
        <span className={styles.rowValue}>
                {homeCourse?.name ?? t('profile.none')}
                <ChevronRightIcon size={15} />
              </span>
      </button>

      <SectionBar>{t('profile.preferences')}</SectionBar>
      <div className={styles.unitRow}>
        <div className={styles.unitHead}>
          <span className={styles.rowStacked}>
            <span className={styles.rowLabel}>{t('profile.distanceUnit')}</span>
            <span className={styles.rowHint}>{t('profile.appliesEverywhere')}</span>
          </span>
          <span className={styles.sheetPills}>
            <Pill selected={unit === 'yd'} onClick={() => changeUnit('yd')}>
              {t('profile.yards')}
            </Pill>
            <Pill selected={unit === 'm'} onClick={() => changeUnit('m')}>
              {t('profile.meters')}
            </Pill>
          </span>
        </div>
      </div>

      {/*
        O seletor fica ao lado da unidade porque as duas são a mesma coisa para quem usa: escolhas
        que valem para o app inteiro e se fazem uma vez. A dica avisa da recarga em vez de a tela
        recarregar sem explicação — ver `lib/i18n/locale.ts`.
      */}
      <div className={styles.unitRow}>
        <div className={styles.unitHead}>
          <span className={styles.rowStacked}>
            <span className={styles.rowLabel}>{t('profile.language')}</span>
            <span className={styles.rowHint}>{t('profile.languageHint')}</span>
          </span>
          <span className={styles.sheetPills}>
            {LANGUAGES.map((l) => (
              <Pill key={l.id} selected={locale === l.id} onClick={() => saveLocale(l.id)}>
                {t(l.label)}
              </Pill>
            ))}
          </span>
        </div>
      </div>

      <div className={styles.row}>
        <span className={styles.rowLabel}>{t('profile.changePassword')}</span>
        <span className={styles.soon}>{t('profile.soon')}</span>
      </div>

      <SectionBar>{t('profile.privacy')}</SectionBar>
      <div className={cx(styles.unitRow, styles.privacyRow)}>
        <div className={styles.unitHead}>
          <span className={styles.rowStacked}>
            <span className={styles.rowLabel}>{t('profile.shareRounds')}</span>
            <span className={styles.rowHint}>{t('profile.shareRoundsHint')}</span>
          </span>
          <span className={styles.sheetPills}>
            <Pill selected={user.shareRoundsWithFriends} onClick={() => void setSharing(true)}>
              {t('profile.on')}
            </Pill>
            <Pill selected={!user.shareRoundsWithFriends} onClick={() => void setSharing(false)}>
              {t('profile.off')}
            </Pill>
          </span>
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" block size="lg" onClick={() => setConfirmLogout(true)}>
          {t('profile.logout')}
        </Button>
        <Button variant="danger" block disabled>
          {t('profile.deleteAccount')}
        </Button>
      </div>
        </div>
      </div>

      <Sheet
        open={editing}
        onClose={() => setEditing(false)}
        title={t('profile.edit')}
        footer={
          <Button
            block
            size="lg"
            onClick={() => void save()}
            busy={saving}
            disabled={!draftName.trim()}
          >
            {t('profile.save')}
          </Button>
        }
      >
        <div className={styles.sheetField}>
          <Field
            label={t('profile.displayName')}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            maxLength={80}
            hint={t('profile.nameHint')}
            showCounter
          />
        </div>

        <SheetGroup label={t('profile.genderLabel')}>
          <div className={styles.sheetPills}>
            {(['M', 'F', 'X'] as Gender[]).map((g) => (
              <Pill key={g} selected={draftGender === g} onClick={() => setDraftGender(g)}>
                {t(GENDER_LABEL[g])}
              </Pill>
            ))}
          </div>
        </SheetGroup>

        <SheetGroup label={t('profile.homeCourse')}>
          <select
            className={styles.select}
            value={draftHome}
            onChange={(e) => setDraftHome(e.target.value)}
          >
            <option value="">{t('profile.none')}</option>
            {[...(courseIndex.data?.values() ?? [])].map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </SheetGroup>
      </Sheet>

      <Dialog
        open={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        title={t('profile.logoutTitle')}
        confirmLabel={t('profile.logout')}
        onConfirm={() => {
          void logout().then(() => navigate(routes.login, { replace: true }));
        }}
        inline
      >
        {t('profile.logoutBody')}
      </Dialog>
    </div>
  );
}

/**
 * "12–14 voltas" — a mesma faixa que a tabela destaca, dita em texto. As fronteiras vêm da
 * mesma lista que a tabela desenha, então as duas não têm como discordar.
 */
const WHS_BANDS = [3, 4, 5, 6, 8, 11, 14, 16, 18, 19, 20];

function bandLabel(rounds: number): string {
  for (let i = 0; i < WHS_BANDS.length; i++) {
    const upper = WHS_BANDS[i] ?? 20;
    const lower = i === 0 ? 3 : (WHS_BANDS[i - 1] ?? 0) + 1;
    if (upper === 20) {
      return rounds >= lower
        ? t('profile.band20plus')
        : t('profile.bandRange', { from: lower, to: upper });
    }
    if (rounds >= lower && rounds <= upper) {
      return lower === upper
        ? t('profile.bandExact', { count: lower })
        : t('profile.bandRange', { from: lower, to: upper });
    }
  }
  return t('profile.bandExact', { count: rounds });
}

/** "Baseado em 12 voltas · 9 ago 2026, 16:44" — a data só entra quando o servidor a manda. */
function handicapMeta(basedOn: number, lastUpdated: string | undefined): string {
  const base = basedOn > 0 ? t('home.basedOn', { count: basedOn }) : t('profile.notEnoughRounds');
  return lastUpdated
    ? t('profile.handicapMeta', { base, date: fmtDateTime(lastUpdated) })
    : base;
}

/**
 * The WHS table, generated from the same functions the differentials chart uses — so what
 * the profile explains and what the chart highlights can never drift apart.
 */
function WhsTable({ currentRounds }: { currentRounds: number }) {
  const bands = WHS_BANDS;

  return (
    <div className={styles.table}>
      <div className={styles.tableHead}>
        <span className={styles.colRounds}>{t('profile.tableRounds')}</span>
        <span className={styles.colBase}>{t('profile.tableBase')}</span>
        <span className={styles.colAdjust}>{t('profile.tableAdjust')}</span>
      </div>
      {bands.map((upper, i) => {
        const lower = i === 0 ? 3 : (bands[i - 1] ?? 0) + 1;
        const take = countingScoreCount(upper);
        const adjust = handicapAdjustment(upper);
        const isCurrent = currentRounds >= lower && (upper === 20 ? true : currentRounds <= upper);
        const label = upper === 20 ? '20+' : lower === upper ? `${lower}` : `${lower}–${upper}`;

        return (
          <div key={upper} className={cx(styles.tableRow, isCurrent && styles.tableRowActive)}>
            <span className={styles.colRounds}>{label}</span>
            <span className={styles.colBase}>
              {take === 1
                ? t('profile.tableLowest1')
                : t('profile.tableAverageOf', { count: take })}
            </span>
            <span className={cx(styles.colAdjust, adjust === 0 && styles.adjustNone)}>
              {adjust === 0 ? '—' : fmtDecimal(adjust)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
